import React, { useState, useEffect } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Hero from "components/lib/Global/Hero";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import Sidebar from "./Sidebar.jsx";
import { MinuteBookContext } from "context/MinuteBookContext";
import { minuteBookSidebarItems } from "./Sidebar.jsx/minuteBookSidebarItems";
import { flattenNestedChildren } from "utils";
import { getAccessRequests } from "state/slices/accessRequest";
import {
  getCompanyBeneficialOwnersAsync,
  getCompanyDashboardAsync,
  getCompanyOfficialsAsync,
  resetCompanyDashboard,
} from "state/slices/companyDashboard";
import BusinessDetails from "./BusinessDetails";
import DashboardWrapper from "components/lib/Global/DashboardWrapper";
import { resetActiveDashboardSlice } from "state/slices/activeDashboard";
import { setCookie } from "utils/cookie";
import { Constants } from "config/constants";
import Verify from "./Verify.jsx";
import { resetCompanyOfficialsTableSlice } from "state/slices/tables/companyOfficialsSlice.js";
import { resetSharesTable } from "state/slices/tables/sharesAllocationTable.js";

//import { useSocket } from "context/SocketContext";

const defaultTab = { id: "businessDetails", name: "Business Details" };

const TransferYourBusiness = () => {
  const { companyId } = useParams();
  const history = useHistory();
  //const socket = useSocket();
  const dispatch = useDispatch();
  const { getUserStatus } = useSelector((state) => state.authSlice);
  const { company, getCompanyStatus, breadCrumbs } = useSelector(
    (state) => state.companyDashboardSlice
  );
  const { companyRegNumber } = useSelector(
    (state) => state.transferYourBusinessSlice
  );

  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const currentUser = useSelector((state) => state.userSlice);
  const [partialData, setPartialData] = useState({});
  const [showBusinessDetails, setShowBusinessDetails] =
    useState(companyRegNumber);
  const [companyExists, setCompanyExists] = useState(false);
  const [entityTypeIsAccepted, setEntityTypeIsAccepted] = useState(true);
  const [companyIsTransfered, setCompanyIsTransfered] = useState(false);

  useEffect(() => {
    dispatch(resetCompanyOfficialsTableSlice());
    dispatch(resetSharesTable());
    dispatch(resetCompanyDashboard());
  }, []);

  useEffect(() => {
    if (companyId && currentUser?._id) {
      dispatch(getCompanyDashboardAsync({ id: companyId }));
      dispatch(
        getCompanyOfficialsAsync({
          companyId,
          queryParams: { isDashboard: true },
        })
      );
      dispatch(getCompanyBeneficialOwnersAsync({ companyId }));
      dispatch(getAccessRequests({ query: { createdBy: currentUser._id } }));
    }
    return () => {
      dispatch(resetCompanyDashboard());
    };
  }, [companyId, currentUser?._id, dispatch]);

  const companyName = company?.registrationName ?? company?.companyName1;

  let pages =
    breadCrumbs.length > 0
      ? [
          { name: "My Companies", href: "/companies", current: false },
          ...breadCrumbs,
        ]
      : [
          { name: "My Companies", href: "/companies", current: false },
          companyName
            ? {
                name: companyName,
                href: "#",
                current: true,
              }
            : null,
        ];

  const goToBusinessDetails = () =>
    setSelectedTab({ id: "businessDetails", name: "Business Details" });

  const handleExitDashboard = () => {
    setCookie("role", Constants.COOKIE_ROLES.APPLICANT);
    dispatch(resetActiveDashboardSlice());
    history.push("/dashboard");
  };

  const isProfileIncomplete = ["Employee", "Co Applicant"].includes(
    currentUser.profileType
  );

  if (isProfileIncomplete) {
    return (
      <DashboardWrapper>
        <div className="flex justify-center items-center h-screen">
          <span className="font-medium text-red-500">
            You cannot transfer your business until your profile is{" "}
            <Link to="/profile" className="underline">
              upgraded.
            </Link>
          </span>
        </div>
      </DashboardWrapper>
    );
  }

  return (
    <>
      <DashboardWrapper
        exitDashboardText="Exit to personal dashboard"
        handleExitDashboard={handleExitDashboard}
      >
        <MinuteBookContext.Provider
          value={{
            selectedTab,
            setSelectedTab,
            individualSidebarItems: flattenNestedChildren(
              minuteBookSidebarItems
            ),
          }}
        >
          {currentUser && currentUser?.isOnboarded && (
            <div className="grid gap-10 py-10 mx-auto">
              <Hero
                heading={
                  company?.registrationName ??
                  selectedTab?.label ??
                  selectedTab?.name
                }
                description=""
              />
              <div className="grid grid-cols-4 border-t border-gray">
                {
                  <Sidebar
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                  />
                }

                <div className="col-span-4 md:border-l md:col-span-3 md:pl-11 pt-8 border-gray">
                  <Verify
                    setPartialData={setPartialData}
                    setShowBusinessDetails={setShowBusinessDetails}
                    companyExists={companyExists}
                    setCompanyExists={setCompanyExists}
                    entityTypeIsAccepted={entityTypeIsAccepted}
                    setEntityTypeIsAccepted={setEntityTypeIsAccepted}
                    companyIsTransfered={companyIsTransfered}
                    setCompanyIsTransfered={setCompanyIsTransfered}
                  />

                  {getUserStatus.status === "loading" ||
                  getCompanyStatus.status === "loading" ? (
                    <div className="flex justify-center mx-auto text-white">
                      <SpinnerIcon textColor="text-gray-400" />
                    </div>
                  ) : (
                    <>
                      {!companyIsTransfered &&
                        showBusinessDetails &&
                        !companyExists &&
                        entityTypeIsAccepted && (
                          <BusinessDetails partialData={partialData} />
                        )}
                    </>
                  )}
                </div>
                <div></div>
              </div>
            </div>
          )}
        </MinuteBookContext.Provider>
      </DashboardWrapper>
    </>
  );
};

export default TransferYourBusiness;
